import React from 'react';
import { motion } from 'framer-motion';
export interface SpeedDiagramProps {
  /** Speed value (km/h or m/s) */
  speed?: number;
  /** Distance value */
  distance?: number;
  /** Time value */
  time?: number;
  /** Unit for speed */
  speedUnit?: string;
  /** Unit for distance */
  distanceUnit?: string;
  /** Unit for time */
  timeUnit?: string;
  /** Which value to highlight/hide (for stimulus mode) */
  unknown?: 'speed' | 'distance' | 'time';
  /** Label text */
  label?: string;
  /** Display mode */
  mode?: 'stimulus' | 'support';
}
function formatValue(value: number | undefined, unit: string): string {
  if (value === undefined) return '?';
  // Use Dutch locale
  const formatted = new Intl.NumberFormat('nl-NL', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0
  }).format(value);
  return `${formatted} ${unit}`;
}
export function SpeedDiagram({
  speed,
  distance,
  time,
  speedUnit = 'km/u',
  distanceUnit = 'km',
  timeUnit = 'uur',
  unknown,
  label,
  mode = 'support'
}: SpeedDiagramProps) {
  const isStimulus = mode === 'stimulus';
  // In stimulus mode, hide the unknown value
  const showSpeed = !(isStimulus && unknown === 'speed');
  const showDistance = !(isStimulus && unknown === 'distance');
  const showTime = !(isStimulus && unknown === 'time');
  const triangleSize = 200;
  const cx = triangleSize / 2;
  const topY = 20;
  const bottomY = triangleSize - 20;
  const leftX = 20;
  const rightX = triangleSize - 20;
  return <div className={`${mode === 'support' ? 'bg-gradient-to-br from-teal-50 to-cyan-50 p-6 rounded-2xl border-2 border-teal-200 shadow-sm' : 'bg-teal-50 rounded-xl px-4 pt-3 pb-5 border border-teal-200'}`} data-id="element-9376">
      {label && <p className={`text-sm font-semibold text-teal-800 mb-3 text-center ${mode === 'support' ? 'text-base' : ''}`} data-id="element-9377">
          {label}
        </p>}

      <div className="flex justify-center" aria-label="Snelheidsdriehoek: afstand = snelheid × tijd" data-id="element-9378">
        <svg width={triangleSize} height={triangleSize} viewBox={`0 0 ${triangleSize} ${triangleSize}`} className="max-w-full" data-id="element-9379">
          {/* Triangle outline */}
          <motion.polygon points={`${cx},${topY} ${leftX},${bottomY} ${rightX},${bottomY}`} fill="none" stroke="#0d9488" strokeWidth={2.5} strokeLinejoin="round" initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          duration: 0.5
        }} data-id="element-9380" />

          {/* Horizontal divider (separates distance from speed×time) */}
          <motion.line x1={leftX + 20} y1={(topY + bottomY) / 2 + 5} x2={rightX - 20} y2={(topY + bottomY) / 2 + 5} stroke="#0d9488" strokeWidth={2} initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          duration: 0.3,
          delay: 0.3
        }} data-id="element-9381" />

          {/* Vertical divider in bottom half */}
          <motion.line x1={cx} y1={(topY + bottomY) / 2 + 5} x2={cx} y2={bottomY - 5} stroke="#0d9488" strokeWidth={2} initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          duration: 0.3,
          delay: 0.4
        }} data-id="element-9382" />

          {/* Distance (top section) */}
          <motion.text x={cx} y={(topY + (topY + bottomY) / 2) / 2 + 10} textAnchor="middle" dominantBaseline="middle" className="text-sm font-bold" fill={unknown === 'distance' ? '#dc2626' : '#0f766e'} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.5
        }} data-id="element-9383">
            {showDistance && distance !== undefined ? formatValue(distance, distanceUnit) : 'Afstand'}
          </motion.text>

          {/* Speed (bottom-left) */}
          <motion.text x={(leftX + cx) / 2 + 5} y={((topY + bottomY) / 2 + 5 + bottomY) / 2} textAnchor="middle" dominantBaseline="middle" className="text-xs font-bold" fill={unknown === 'speed' ? '#dc2626' : '#0f766e'} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.6
        }} data-id="element-9384">
            {showSpeed && speed !== undefined ? formatValue(speed, speedUnit) : 'Snelheid'}
          </motion.text>

          {/* Multiplication sign */}
          <motion.text x={cx} y={((topY + bottomY) / 2 + 5 + bottomY) / 2} textAnchor="middle" dominantBaseline="middle" className="text-sm font-bold" fill="#94a3b8" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.55
        }} data-id="element-9385">
            ×
          </motion.text>

          {/* Time (bottom-right) */}
          <motion.text x={(cx + rightX) / 2 - 5} y={((topY + bottomY) / 2 + 5 + bottomY) / 2} textAnchor="middle" dominantBaseline="middle" className="text-xs font-bold" fill={unknown === 'time' ? '#dc2626' : '#0f766e'} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.7
        }} data-id="element-9386">
            {showTime && time !== undefined ? formatValue(time, timeUnit) : 'Tijd'}
          </motion.text>
        </svg>
      </div>

      {/* Formula explanation in support mode */}
      {mode === 'support' && <div className="mt-4 space-y-2" data-id="element-9387">
          <div className="bg-white/80 rounded-xl p-4 border border-teal-100" data-id="element-9388">
            <div className="flex items-start gap-2" data-id="element-9389">
              <span className="text-lg" data-id="element-9390">📐</span>
              <div className="text-sm text-slate-700" data-id="element-9391">
                <p className="font-semibold text-teal-800 mb-1" data-id="element-9392">
                  De snelheidsdriehoek
                </p>
                <div className="space-y-1" data-id="element-9393">
                  <p data-id="element-9394">
                    <strong data-id="element-9395">Afstand</strong> = snelheid × tijd
                  </p>
                  <p data-id="element-9396">
                    <strong data-id="element-9397">Snelheid</strong> = afstand ÷ tijd
                  </p>
                  <p data-id="element-9398">
                    <strong data-id="element-9399">Tijd</strong> = afstand ÷ snelheid
                  </p>
                </div>
                {unknown && <p className="mt-2 text-teal-700 font-medium" data-id="element-9400">
                    {unknown === 'speed' && speed !== undefined && <>
                        Snelheid = {formatValue(distance, distanceUnit)} ÷{' '}
                        {formatValue(time, timeUnit)} ={' '}
                        {formatValue(speed, speedUnit)}
                      </>}
                    {unknown === 'distance' && distance !== undefined && <>
                        Afstand = {formatValue(speed, speedUnit)} ×{' '}
                        {formatValue(time, timeUnit)} ={' '}
                        {formatValue(distance, distanceUnit)}
                      </>}
                    {unknown === 'time' && time !== undefined && <>
                        Tijd = {formatValue(distance, distanceUnit)} ÷{' '}
                        {formatValue(speed, speedUnit)} ={' '}
                        {formatValue(time, timeUnit)}
                      </>}
                  </p>}
              </div>
            </div>
          </div>
        </div>}
    </div>;
}